// Bank rails — live data from useBankAccounts(). Phase 3.
//
// Security constraints (CLAUDE.md + T-3-15):
//   - Credentials (password, merchant_key) are type="password" inputs only.
//   - Credential state is cleared on every modal close — never persisted, never
//     rendered back into the table, never written to local/sessionStorage.
//   - Money display uses fmtCcyMinor (bigint paise) — never float coercion.
//   - No env-picker field (D-02). No account_no / ifsc fields (D-05).
import { useState, type FormEvent } from "react";
import axios from "axios";

import { I } from "@/components/Icons";
import { Modal, StatusPill } from "@/components/UI";
import { formatDateTime } from "@/utils/format";
import { useBankAccounts } from "@/queries/useBankAccounts";
import type { BankProvider, FileRailSettings } from "@/api/bankAccounts";
import { apiErrorMessage } from "@/utils/apiError";
import { useAuth, isAdminRole } from "@/store/auth";
import { useUI } from "@/store/ui";
import { useQueryClient } from "@tanstack/react-query";
import { providerLabel } from "@/utils/providerLabel";
import {
  createBankAccount,
  rotateBankAccountCredentials,
  updateBankAccount,
  verifyBankAccount,
  type BankAccount,
  type VerifyResult,
} from "@/api/bankAccounts";
import {
  createBuckzyCredential,
  type BuckzyCredentialPayload,
} from "@/api/buckzy";
import {
  canPickRailEnvironment,
  defaultRailEnvironment,
  lockedRailEnvironmentLabel,
} from "@/utils/deployEnv";

// ─── Credential-field state helpers ──────────────────────────────────────────

interface CredFields {
  email: string;
  password: string;
  merchant_key: string;
}

const EMPTY_CREDS: CredFields = { email: "", password: "", merchant_key: "" };

// Buckzy credential field set (D-04). [ASSUMED] from the Corefy connector docs
// pending OQ-01 — if the live Buckzy contract differs, the field list adjusts
// but the security invariants (type=password, resetAndClose, no storage) do not.
// AUDIT-PASS-9 follow-up: extended with merchant_party_id + settlement_account_id.
const EMPTY_BUCKZY_CREDS: BuckzyCredentialPayload = {
  client_id: "",
  client_secret: "",
  api_key: "",
  username: "",
  password: "",
  api_url: "",
  auth_url: "",
  creditor_reference_id: "",
  merchant_party_id: "",
  settlement_account_id: "",
};

// ─── Error-code extraction (mirrors Login.tsx otpErrorMessage pattern) ────────

function extractErrorCode(err: unknown): string | undefined {
  if (axios.isAxiosError(err)) {
    return (err.response?.data as { code?: string } | undefined)?.code;
  }
  return undefined;
}

// ─── Add bank rail modal ──────────────────────────────────────────────────────

interface AddModalProps {
  open: boolean;
  onClose: () => void;
  merchantId: number;
}

function AddRailModal({ open, onClose, merchantId }: AddModalProps) {
  const qc = useQueryClient();

  // Reuses the API's own union rather than restating it, so adding a rail
  // there cannot leave this select silently unable to hold it.
  const [provider, setProvider] = useState<BankProvider>("paysmart_idfc");
  const [label, setLabel] = useState("");
  // Canara-rail ordering block (migration 048). These appear on every record
  // of the bulk file and are what the payee sees on their statement; the bank
  // refuses to generate a file without the name and first address line.
  const [orderingName, setOrderingName] = useState("");
  const [orderingAddr1, setOrderingAddr1] = useState("");
  const [orderingAddr2, setOrderingAddr2] = useState("");
  const [senderInfo, setSenderInfo] = useState("");
  const [creds, setCreds] = useState<CredFields>(EMPTY_CREDS);
  const [baseUrl, setBaseUrl] = useState("");
  // Issue #1: bank-rail-issued merchant_id, distinct from the operator-chosen
  // label. Required when provider=paysmart_idfc; the backend 400s with
  // code=provider_merchant_id_missing if empty.
  const [providerMerchantId, setProviderMerchantId] = useState("");
  // RazorpayX credential triple (Cut 1). Only required when
  // provider=razorpayx_inr; backend 400s with razorpay_credentials_missing
  // on any empty field.
  const [razorpayKeyID, setRazorpayKeyID] = useState("");
  const [razorpayKeySecret, setRazorpayKeySecret] = useState("");
  const [razorpayAccountNumber, setRazorpayAccountNumber] = useState("");
  // Touras rail. The key is the only secret; it never leaves component state
  // except in the create request (same invariant as the other credentials).
  const [tourasKey, setTourasKey] = useState("");
  const [tourasUID, setTourasUID] = useState("");
  const [tourasBaseUrl, setTourasBaseUrl] = useState("");
  const [tourasMobile, setTourasMobile] = useState("");
  const [tourasEmail, setTourasEmail] = useState("");
  // Manual rail: the account the transfers are DEBITED from. Not credentials —
  // a bank with no API has nothing to authenticate to. These end up on every
  // row of the CSV an operator uploads, which is why the backend requires them
  // (code=manual_account_details_missing) rather than treating them as
  // optional metadata.
  const [manualAccountNo, setManualAccountNo] = useState("");
  const [manualIFSC, setManualIFSC] = useState("");
  // PDF round 3 N4: default + lock the environment based on which deploy this
  // bundle was built for. Staging → sandbox-only, prod → production-only, dev →
  // operator picks. canPickRailEnvironment() returns false outside dev so
  // the picker collapses to a read-only hint below.
  const [environment, setEnvironment] = useState<"sandbox" | "production">(
    defaultRailEnvironment(),
  );
  const [submitting, setSubmitting] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [succeeded, setSucceeded] = useState(false);

  function resetAndClose() {
    // Clear all credential state before closing (T-3-15).
    setProvider("paysmart_idfc");
    setLabel("");
    setCreds(EMPTY_CREDS);
    setBaseUrl("");
    setProviderMerchantId("");
    setRazorpayKeyID("");
    setRazorpayKeySecret("");
    setRazorpayAccountNumber("");
    setTourasKey("");
    setTourasUID("");
    setTourasBaseUrl("");
    setTourasMobile("");
    setTourasEmail("");
    setEnvironment(defaultRailEnvironment());
    setSubmitting(false);
    setVerifyError(null);
    setGeneralError(null);
    setSucceeded(false);
    onClose();
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setVerifyError(null);
    setGeneralError(null);
    setSubmitting(true);

    try {
      // AUDIT-PASS-9: build the payload conditionally. Buckzy rails carry
      // NO per-rail bank-rail credentials — those fields are omitted from
      // the request body so the backend's conditional-required check skips
      // them, and the backend service routes to CreateBuckzy (no
      // provider_credentials seal).
      const payload =
        provider === "buckzy_global_payout"
          ? { label: label.trim(), provider }
          : provider === "manual" || provider === "canara_bank"
          ? {
              // Both file rails carry the debit-side account the bulk file
              // is drawn on, and nothing else — there are no credentials to
              // seal for a rail with no API.
              label: label.trim(),
              provider,
              manual_account_no: manualAccountNo.trim(),
              manual_ifsc: manualIFSC.trim().toUpperCase(),
              // Only the fixed-format rail stamps an ordering block; the
              // generic manual rail renders an admin-defined grid with none.
              ...(provider === "canara_bank"
                ? {
                    file_rail_settings: {
                      ordering_customer_name: orderingName.trim(),
                      address_line_1: orderingAddr1.trim(),
                      address_line_2: orderingAddr2.trim(),
                      // The payee's own bank. Payees here settle as an
                      // internal transfer rather than NEFT.
                      on_us_ifsc_prefix: "CNRB",
                      sender_to_receiver_info: senderInfo.trim(),
                    },
                  }
                : {}),
            }
          : provider === "razorpayx_inr"
            ? {
                label: label.trim(),
                provider,
                razorpay_key_id: razorpayKeyID.trim(),
                razorpay_key_secret: razorpayKeySecret.trim(),
                razorpay_account_number: razorpayAccountNumber.trim(),
              }
            : provider === "touras_inr"
            ? {
                label: label.trim(),
                provider,
                touras_encryption_key: tourasKey.trim(),
                touras_uid: tourasUID.trim(),
                touras_base_url: tourasBaseUrl.trim(),
                touras_contact_mobile: tourasMobile.trim(),
                touras_contact_email: tourasEmail.trim(),
              }
            : {
                label: label.trim(),
                provider,
                email: creds.email.trim(),
                password: creds.password,
                merchant_key: creds.merchant_key,
                base_url: baseUrl.trim(),
                environment,
                provider_merchant_id: providerMerchantId.trim(),
              };
      const result = await createBankAccount(merchantId, payload);

      if (result.active) {
        setSucceeded(true);
        void qc.invalidateQueries({ queryKey: ["bank-accounts", merchantId] });
        setTimeout(() => resetAndClose(), 1200);
      } else {
        // Rail created but credentials failed verification — keep modal open.
        setVerifyError(
          result.verify_error ??
            "Credentials could not be verified. The rail was created but is inactive — retry by rotating credentials.",
        );
      }
    } catch (err) {
      const code = extractErrorCode(err);
      if (code === "base_url_invalid") {
        setGeneralError("The base URL must be a valid https URL.");
      } else if (code === "environment_invalid") {
        setGeneralError("Select a valid environment (sandbox or production).");
      } else if (code === "provider_merchant_id_missing") {
        setGeneralError(
          "Bank merchant ID is required. It's the alphanumeric ID the bank rail issued during onboarding (e.g. 29V2155SVGITOGND).",
        );
      } else if (code === "razorpay_credentials_missing") {
        setGeneralError(
          "Razorpay key id, key secret, and account number are all required.",
        );
      } else {
        setGeneralError(
          "An unexpected error occurred. Please try again or contact support.",
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={resetAndClose}
      busy={submitting}
      title="Add bank rail"
      foot={
        succeeded ? (
          <div className="row gap-8" style={{ color: "var(--pos)", fontSize: 13 }}>
            <I.Check size={14} stroke={2} /> Rail created and active
          </div>
        ) : verifyError ? (
          <button className="btn" type="button" onClick={resetAndClose}>
            Close
          </button>
        ) : (
          <>
            <button
              className="btn ghost"
              type="button"
              onClick={resetAndClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <div className="flex-1" />
            <button
              className="btn accent"
              type="submit"
              form="add-rail-form"
              disabled={
                submitting ||
                !label.trim() ||
                // Buckzy: only label is required; the merchant's apikey lives
                // in buckzy_credentials (separate handler), not on this row.
                (provider === "paysmart_idfc" &&
                  (!baseUrl.trim() ||
                    !creds.email.trim() ||
                    !creds.password ||
                    !creds.merchant_key ||
                    !providerMerchantId.trim())) ||
                (provider === "razorpayx_inr" &&
                  (!razorpayKeyID.trim() ||
                    !razorpayKeySecret.trim() ||
                    !razorpayAccountNumber.trim())) ||
                (provider === "touras_inr" &&
                  (!tourasKey.trim() ||
                    !tourasUID.trim() ||
                    !tourasMobile.trim() ||
                    !tourasEmail.trim())) ||
                ((provider === "manual" || provider === "canara_bank") &&
                  (!manualAccountNo.trim() || !manualIFSC.trim())) ||
                (provider === "canara_bank" &&
                  (!orderingName.trim() || !orderingAddr1.trim()))
              }
            >
              {submitting ? "Creating…" : "Create rail"}
            </button>
          </>
        )
      }
    >
      <form id="add-rail-form" onSubmit={handleSubmit} className="stack-16">
        {/* AUDIT-PASS-9: provider selector — IDFC bank rail OR Buckzy. */}
        <div className="field">
          <label className="label" htmlFor="add-rail-provider">
            Provider
          </label>
          <select
            id="add-rail-provider"
            className="input"
            value={provider}
            onChange={(e) =>
              setProvider(
                e.target.value as BankProvider,
              )
            }
          >
            <option value="paysmart_idfc">IDFC Bank (IMPS/NEFT)</option>
            <option value="buckzy_global_payout">Buckzy global payout</option>
            <option value="razorpayx_inr">RazorpayX (India)</option>
            <option value="touras_inr">Touras (India, IMPS/NEFT)</option>
            <option value="manual">Manual — CSV file to the bank</option>
            <option value="canara_bank">Canara Bank — bulk file upload</option>
          </select>
          <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
            {provider === "canara_bank"
              ? "No API. Payouts wait until a superadmin exports them as the bank's own bulk-upload file, uploads it to the corporate portal, and records the outcome. The file layout is fixed by the bank — unlike the generic manual rail, there is nothing to configure."
              : provider === "manual"
              ? "No API. Payouts on this rail wait until a superadmin exports them as a CSV under Manual payouts, uploads it to the bank, and records the outcome."
              : provider === "paysmart_idfc"
              ? "Per-rail credentials are stored per bank rail."
              : provider === "razorpayx_inr"
                ? "Per-rail RazorpayX credentials — key id + secret + the X virtual account number assigned at activation."
                : provider === "touras_inr"
                ? "Per-rail Touras credentials — the encryption key Touras emailed and your user id. Touras only answers whitelisted server IPs: share this environment's IP with Touras before the first payout."
                : "Buckzy uses per-merchant credentials set on the Buckzy credentials card below. Create them first if you haven't."}
          </div>
        </div>

        <div className="field">
          <label className="label" htmlFor="add-rail-label">
            Label
          </label>
          <input
            id="add-rail-label"
            className="input"
            type="text"
            placeholder={
              provider === "paysmart_idfc"
                ? "e.g. Northwind primary"
                : "e.g. Buckzy India payouts"
            }
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            autoComplete="off"
            required
          />
        </div>

        {provider === "paysmart_idfc" && (
          <>
            <div className="field">
              <label className="label" htmlFor="add-rail-env">
                Environment
              </label>
              {canPickRailEnvironment() ? (
                <select
                  id="add-rail-env"
                  className="input"
                  value={environment}
                  onChange={(e) =>
                    setEnvironment(e.target.value as "sandbox" | "production")
                  }
                >
                  <option value="sandbox">Sandbox</option>
                  <option value="production">Production</option>
                </select>
              ) : (
                // PDF round 3 N4: env is locked to match this deploy. Show a
                // read-only chip instead of a useless single-option <select>.
                <div
                  id="add-rail-env"
                  className="input"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    background: "var(--bg-sunk)",
                    color: "var(--ink-2)",
                    cursor: "default",
                  }}
                >
                  <span>{lockedRailEnvironmentLabel()}</span>
                  <span className="dim" style={{ fontSize: 11 }}>
                    · locked to match this deployment
                  </span>
                </div>
              )}
            </div>

            <div className="field">
              <label className="label" htmlFor="add-rail-base-url">
                Bank rail base URL
              </label>
              <input
                id="add-rail-base-url"
                className="input mono"
                type="url"
                placeholder="https://sandbox.yajo.in/"
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                autoComplete="off"
                required
              />
              <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
                Must be an https URL. Each merchant's rail uses its own endpoint.
              </div>
            </div>

            {/* Issue #1: bank-rail-issued merchant_id. Surfaced as its own
                field so operators stop overloading Label as the upstream id.
                Backend rejects empty/whitespace with provider_merchant_id_missing. */}
            <div className="field">
              <label className="label" htmlFor="add-rail-provider-mid">
                Bank merchant ID
              </label>
              <input
                id="add-rail-provider-mid"
                className="input mono"
                type="text"
                placeholder="e.g. 29V2155SVGITOGND"
                value={providerMerchantId}
                onChange={(e) => setProviderMerchantId(e.target.value)}
                autoComplete="off"
                required
              />
              <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
                The alphanumeric ID the bank rail issued during onboarding —
                distinct from the label above. Used as <code>merchant_id</code>
                in balance and payout calls; if it's wrong every upstream
                request returns <code>072 "Empty or invalid merchant id"</code>.
              </div>
            </div>
          </>
        )}

        {provider === "buckzy_global_payout" && (
          <div
            style={{
              background: "var(--bg-sunk)",
              borderLeft: "2px solid var(--hair)",
              padding: "12px 14px",
              borderRadius: 3,
              fontSize: 12,
              color: "var(--ink-2)",
              lineHeight: 1.5,
            }}
          >
            <strong style={{ fontSize: 12, fontWeight: 600 }}>
              Buckzy credentials are per-merchant
            </strong>
            <p style={{ margin: "4px 0 0 0" }}>
              Bank rails on the Buckzy provider don't carry their own credential
              triple. The merchant's apikey + OAuth credentials live on the
              Buckzy credentials card lower on this page. If you haven't set
              those yet, the rail will create but payouts will fail until you do.
            </p>
          </div>
        )}

        {(provider === "manual" || provider === "canara_bank") && (
          <div
            style={{
              background: "var(--bg-sunk)",
              borderLeft: "2px solid var(--hair)",
              padding: "12px 14px",
              borderRadius: 3,
            }}
          >
            <div className="field">
              <label className="label" htmlFor="add-rail-manual-acct">
                Debit account number
              </label>
              <input
                id="add-rail-manual-acct"
                className="input"
                value={manualAccountNo}
                onChange={(e) => setManualAccountNo(e.target.value)}
                placeholder="The account the transfers leave from"
                autoComplete="off"
              />
            </div>
            <div className="field" style={{ marginTop: 10 }}>
              <label className="label" htmlFor="add-rail-manual-ifsc">
                Debit IFSC
              </label>
              <input
                id="add-rail-manual-ifsc"
                className="input"
                value={manualIFSC}
                onChange={(e) => setManualIFSC(e.target.value)}
                placeholder="e.g. HDFC0001234"
                autoComplete="off"
                style={{ textTransform: "uppercase" }}
              />
            </div>
            <p className="dim" style={{ fontSize: 11, marginTop: 8 }}>
              These are not credentials — a bank with no API has nothing to
              authenticate to. They go on every row of the payment file so the
              bank knows which account to debit.
            </p>

            {provider === "canara_bank" && (
              <div style={{ marginTop: 14 }}>
                <div className="field">
                  <label className="label" htmlFor="add-rail-ordering-name">
                    Ordering customer name
                  </label>
                  <input
                    id="add-rail-ordering-name"
                    className="input"
                    value={orderingName}
                    onChange={(e) => setOrderingName(e.target.value)}
                    placeholder="The name the payee sees on their statement"
                    autoComplete="off"
                  />
                </div>
                <div className="field" style={{ marginTop: 10 }}>
                  <label className="label" htmlFor="add-rail-ordering-addr1">
                    Address line 1
                  </label>
                  <input
                    id="add-rail-ordering-addr1"
                    className="input"
                    value={orderingAddr1}
                    onChange={(e) => setOrderingAddr1(e.target.value)}
                    placeholder="Required — the bank rejects a file without it"
                    autoComplete="off"
                  />
                </div>
                <div className="field" style={{ marginTop: 10 }}>
                  <label className="label" htmlFor="add-rail-ordering-addr2">
                    Address line 2 <span className="dim">(optional)</span>
                  </label>
                  <input
                    id="add-rail-ordering-addr2"
                    className="input"
                    value={orderingAddr2}
                    onChange={(e) => setOrderingAddr2(e.target.value)}
                    autoComplete="off"
                  />
                </div>
                <div className="field" style={{ marginTop: 10 }}>
                  <label className="label" htmlFor="add-rail-sender-info">
                    Statement narration <span className="dim">(optional)</span>
                  </label>
                  <input
                    id="add-rail-sender-info"
                    className="input"
                    value={senderInfo}
                    onChange={(e) => setSenderInfo(e.target.value)}
                    placeholder="Defaults to PAYOUT"
                    autoComplete="off"
                  />
                </div>
                <p className="dim" style={{ fontSize: 11, marginTop: 8 }}>
                  Stamped on every record of the bulk file. The name and first
                  address line are mandatory — the bank will not accept a file
                  without them.
                </p>
              </div>
            )}
          </div>
        )}

        {provider === "razorpayx_inr" && (
          <div
            style={{
              background: "var(--bg-sunk)",
              borderLeft: "2px solid var(--hair)",
              padding: "12px 14px",
              borderRadius: 3,
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 10 }}>
              RazorpayX credentials
            </div>
            <div className="stack-12">
              <div className="field">
                <label className="label" htmlFor="add-rail-rzp-key-id">
                  Key ID
                </label>
                <input
                  id="add-rail-rzp-key-id"
                  className="input mono"
                  type="text"
                  placeholder="rzp_live_…"
                  value={razorpayKeyID}
                  onChange={(e) => setRazorpayKeyID(e.target.value)}
                  autoComplete="off"
                  required
                />
              </div>
              <div className="field">
                <label className="label" htmlFor="add-rail-rzp-secret">
                  Key Secret
                </label>
                <input
                  id="add-rail-rzp-secret"
                  className="input"
                  type="password"
                  autoComplete="new-password"
                  value={razorpayKeySecret}
                  onChange={(e) => setRazorpayKeySecret(e.target.value)}
                  required
                />
              </div>
              <div className="field">
                <label className="label" htmlFor="add-rail-rzp-acct">
                  Account number (RazorpayX virtual account)
                </label>
                <input
                  id="add-rail-rzp-acct"
                  className="input mono"
                  type="text"
                  placeholder="e.g. 2323230012345"
                  value={razorpayAccountNumber}
                  onChange={(e) => setRazorpayAccountNumber(e.target.value)}
                  autoComplete="off"
                  required
                />
                <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
                  The RazorpayX virtual account assigned at X activation —
                  not your underlying settlement bank account. Find it at
                  dashboard.razorpay.com → X → Account.
                </div>
              </div>
            </div>
          </div>
        )}

        {provider === "touras_inr" && (
          <div
            style={{
              background: "var(--bg-sunk)",
              borderLeft: "2px solid var(--hair)",
              padding: "12px 14px",
              borderRadius: 3,
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 10 }}>
              Touras credentials
            </div>
            <div className="stack-12">
              <div className="field">
                <label className="label" htmlFor="add-rail-touras-key">
                  Encryption key
                </label>
                <input
                  id="add-rail-touras-key"
                  className="input"
                  type="password"
                  autoComplete="new-password"
                  value={tourasKey}
                  onChange={(e) => setTourasKey(e.target.value)}
                  required
                />
                <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
                  The base64 key Touras sent to the registered email (ends in
                  “=”).
                </div>
              </div>
              <div className="field">
                <label className="label" htmlFor="add-rail-touras-uid">
                  User ID
                </label>
                <input
                  id="add-rail-touras-uid"
                  className="input mono"
                  type="text"
                  placeholder="AGEN…"
                  value={tourasUID}
                  onChange={(e) => setTourasUID(e.target.value)}
                  autoComplete="off"
                  required
                />
                <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
                  Also used as the session id and merchant id, which is how
                  Touras issues them.
                </div>
              </div>
              <div className="field">
                <label className="label" htmlFor="add-rail-touras-url">
                  API URL
                </label>
                <input
                  id="add-rail-touras-url"
                  className="input mono"
                  type="url"
                  placeholder="Leave empty for UAT (neodev2.touras.in)"
                  value={tourasBaseUrl}
                  onChange={(e) => setTourasBaseUrl(e.target.value)}
                  autoComplete="off"
                />
                <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
                  Touras issues the production URL at go-live.
                </div>
              </div>
              <div className="row gap-12">
                <div className="field flex-1">
                  <label className="label" htmlFor="add-rail-touras-mobile">
                    Fallback mobile
                  </label>
                  <input
                    id="add-rail-touras-mobile"
                    className="input mono"
                    type="tel"
                    inputMode="numeric"
                    placeholder="10 digits"
                    value={tourasMobile}
                    onChange={(e) => setTourasMobile(e.target.value)}
                    required
                  />
                </div>
                <div className="field flex-1">
                  <label className="label" htmlFor="add-rail-touras-email">
                    Fallback email
                  </label>
                  <input
                    id="add-rail-touras-email"
                    className="input"
                    type="email"
                    value={tourasEmail}
                    onChange={(e) => setTourasEmail(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="dim" style={{ fontSize: 11 }}>
                Touras requires a mobile number and email on every payout. A
                payee's own are used when on file; these are sent otherwise.
              </div>
            </div>
          </div>
        )}

        {provider === "paysmart_idfc" && (
        <div
          style={{
            background: "var(--bg-sunk)",
            borderLeft: "2px solid var(--hair)",
            padding: "12px 14px",
            borderRadius: 3,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 10 }}>
            Bank rail credentials
          </div>
          <div className="stack-12">
            <div className="field">
              <label className="label" htmlFor="add-rail-email">
                User ID / Email
              </label>
              <input
                id="add-rail-email"
                className="input mono"
                type="text"
                autoComplete="username"
                value={creds.email}
                onChange={(e) =>
                  setCreds((p) => ({ ...p, email: e.target.value }))
                }
                required
              />
            </div>

            <div className="field">
              <label className="label" htmlFor="add-rail-password">
                Password
              </label>
              <input
                id="add-rail-password"
                className="input"
                type="password"
                autoComplete="new-password"
                value={creds.password}
                onChange={(e) =>
                  setCreds((p) => ({ ...p, password: e.target.value }))
                }
                required
              />
            </div>

            <div className="field">
              <label className="label" htmlFor="add-rail-merchant-key">
                Merchant key
              </label>
              <input
                id="add-rail-merchant-key"
                className="input mono"
                type="password"
                autoComplete="new-password"
                value={creds.merchant_key}
                onChange={(e) =>
                  setCreds((p) => ({ ...p, merchant_key: e.target.value }))
                }
                required
              />
            </div>
          </div>
        </div>
        )}

        {/* Inline error states */}
        {verifyError && (
          <div
            // AUDIT-PASS-6 P-1: verifyError is an *error* (credentials were
            // rejected by the provider), not a caution. The design system
            // uses --warn (amber) for advisories and --neg (red) for
            // failures — the generalError branch below already uses --neg
            // for the same severity. Aligning.
            style={{
              background: "var(--neg-soft)",
              color: "var(--neg)",
              padding: "10px 12px",
              borderRadius: 3,
              fontSize: 12,
            }}
          >
            <strong>Credentials could not be verified:</strong> {verifyError}.
            The rail was created but is inactive — retry by rotating credentials.
          </div>
        )}

        {generalError && (
          <div
            style={{
              color: "var(--neg)",
              fontSize: 12,
            }}
          >
            {generalError}
          </div>
        )}
      </form>
    </Modal>
  );
}

// ─── Add Buckzy connection modal ─────────────────────────────────────────────
//
// Phase-7 D-04. Mirrors AddRailModal: credential entry, type="password" on all
// secret fields, resetAndClose clears every field before onClose(), no browser
// storage write, BFF-only POST via createBuckzyCredential.

function AddBuckzyModal({ open, onClose, merchantId }: AddModalProps) {
  const qc = useQueryClient();

  const [creds, setCreds] = useState<BuckzyCredentialPayload>(
    EMPTY_BUCKZY_CREDS,
  );
  const [submitting, setSubmitting] = useState(false);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [succeeded, setSucceeded] = useState(false);

  function resetAndClose() {
    // Clear all credential state before closing (security invariant 2).
    setCreds(EMPTY_BUCKZY_CREDS);
    setSubmitting(false);
    setGeneralError(null);
    setSucceeded(false);
    onClose();
  }

  function setField(key: keyof BuckzyCredentialPayload, value: string) {
    setCreds((p) => ({ ...p, [key]: value }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setGeneralError(null);
    setSubmitting(true);

    try {
      await createBuckzyCredential(merchantId, creds);
      setSucceeded(true);
      void qc.invalidateQueries({ queryKey: ["balance", merchantId] });
      setTimeout(() => resetAndClose(), 1200);
    } catch (err) {
      const code = extractErrorCode(err);
      if (code === "api_url_invalid") {
        setGeneralError("The API base URL must be a valid https URL.");
      } else if (code === "auth_url_invalid") {
        setGeneralError("The auth URL must be a valid https URL.");
      } else if (code === "buckzy_credential_exists") {
        setGeneralError(
          "This merchant already has a Buckzy connection.",
        );
      } else {
        setGeneralError(
          "An unexpected error occurred. Please try again or contact support.",
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  const incomplete = Object.values(creds).some((v) => !v.trim());

  return (
    <Modal
      open={open}
      onClose={resetAndClose}
      busy={submitting}
      title="Add Buckzy connection"
      foot={
        succeeded ? (
          <div className="row gap-8" style={{ color: "var(--pos)", fontSize: 13 }}>
            <I.Check size={14} stroke={2} /> Connection created
          </div>
        ) : (
          <>
            <button
              className="btn ghost"
              type="button"
              onClick={resetAndClose}
              disabled={submitting}
            >
              Discard
            </button>
            <div className="flex-1" />
            <button
              className="btn accent"
              type="submit"
              form="add-buckzy-form"
              disabled={submitting || incomplete}
            >
              {submitting ? "Adding…" : "Add connection"}
            </button>
          </>
        )
      }
    >
      <form id="add-buckzy-form" onSubmit={handleSubmit} className="stack-16">
        {/* Provider display — static; Buckzy is the only collections provider */}
        <div className="field">
          <label className="label">Provider</label>
          <div
            className="input"
            style={{ color: "var(--ink-3)", userSelect: "none" }}
            aria-readonly="true"
          >
            Buckzy
          </div>
          <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
            Buckzy is a collections provider. Credentials are envelope-encrypted
            and never stored in plain text.
          </div>
        </div>

        <div className="field">
          <label className="label" htmlFor="add-buckzy-api-url">
            API base URL
          </label>
          <input
            id="add-buckzy-api-url"
            className="input mono"
            type="url"
            placeholder="https://…"
            value={creds.api_url}
            onChange={(e) => setField("api_url", e.target.value)}
            autoComplete="off"
            required
          />
          <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
            Must be an https URL. Stored per merchant.
          </div>
        </div>

        <div className="field">
          <label className="label" htmlFor="add-buckzy-auth-url">
            Auth base URL
          </label>
          <input
            id="add-buckzy-auth-url"
            className="input mono"
            type="url"
            placeholder="https://…"
            value={creds.auth_url}
            onChange={(e) => setField("auth_url", e.target.value)}
            autoComplete="off"
            required
          />
          <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
            OAuth token endpoint base URL. Must be https.
          </div>
        </div>

        <div
          style={{
            background: "var(--bg-sunk)",
            borderLeft: "2px solid var(--hair)",
            padding: "12px 14px",
            borderRadius: 3,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 10 }}>
            Buckzy credentials
          </div>
          <div className="stack-12">
            <div className="field">
              <label className="label" htmlFor="add-buckzy-client-id">
                Client ID
              </label>
              <input
                id="add-buckzy-client-id"
                className="input mono"
                type="text"
                autoComplete="off"
                value={creds.client_id}
                onChange={(e) => setField("client_id", e.target.value)}
                required
              />
            </div>

            <div className="field">
              <label className="label" htmlFor="add-buckzy-client-secret">
                Client secret
              </label>
              <input
                id="add-buckzy-client-secret"
                className="input"
                type="password"
                autoComplete="new-password"
                value={creds.client_secret}
                onChange={(e) => setField("client_secret", e.target.value)}
                required
              />
            </div>

            <div className="field">
              <label className="label" htmlFor="add-buckzy-api-key">
                API key
              </label>
              <input
                id="add-buckzy-api-key"
                className="input"
                type="password"
                autoComplete="new-password"
                value={creds.api_key}
                onChange={(e) => setField("api_key", e.target.value)}
                required
              />
            </div>

            <div className="field">
              <label className="label" htmlFor="add-buckzy-username">
                Username
              </label>
              <input
                id="add-buckzy-username"
                className="input"
                type="text"
                autoComplete="username"
                value={creds.username}
                onChange={(e) => setField("username", e.target.value)}
                required
              />
            </div>

            <div className="field">
              <label className="label" htmlFor="add-buckzy-password">
                Password
              </label>
              <input
                id="add-buckzy-password"
                className="input"
                type="password"
                autoComplete="new-password"
                value={creds.password}
                onChange={(e) => setField("password", e.target.value)}
                required
              />
            </div>

            <div className="field">
              <label className="label" htmlFor="add-buckzy-merchant-party">
                Merchant party ID
              </label>
              <input
                id="add-buckzy-merchant-party"
                className="input mono"
                type="text"
                autoComplete="off"
                placeholder="optional — required for payouts"
                value={creds.merchant_party_id ?? ""}
                onChange={(e) =>
                  setField("merchant_party_id", e.target.value)
                }
              />
              <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
                Buckzy's party reference for this merchant
                (debtor.reference on payouts). Leave blank if you only need
                collections — payouts will surface a clear error until set.
              </div>
            </div>

            <div className="field">
              <label className="label" htmlFor="add-buckzy-settlement-account">
                Settlement account ID
              </label>
              <input
                id="add-buckzy-settlement-account"
                className="input mono"
                type="text"
                autoComplete="off"
                placeholder="optional — required for payouts"
                value={creds.settlement_account_id ?? ""}
                onChange={(e) =>
                  setField("settlement_account_id", e.target.value)
                }
              />
              <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
                Buckzy's settlement account reference used for
                debtorAccount.reference + settlementInformation.
              </div>
            </div>

            <div className="field">
              <label className="label" htmlFor="add-buckzy-creditor-ref">
                Creditor reference ID
                <span className="dim" style={{ fontWeight: 400, marginLeft: 6 }}>
                  (legacy)
                </span>
              </label>
              <input
                id="add-buckzy-creditor-ref"
                className="input mono"
                type="text"
                autoComplete="off"
                placeholder="legacy — merchant_party_id is preferred"
                value={creds.creditor_reference_id ?? ""}
                onChange={(e) =>
                  setField("creditor_reference_id", e.target.value)
                }
              />
            </div>
          </div>
        </div>

        {/* Inline error state */}
        {generalError && (
          <div style={{ color: "var(--neg)", fontSize: 12 }}>{generalError}</div>
        )}
      </form>
    </Modal>
  );
}

// ─── Rotate credentials modal ─────────────────────────────────────────────────

interface RotateModalProps {
  open: boolean;
  onClose: () => void;
  merchantId: number;
  rail: BankAccount | null;
}

function RotateCredentialsModal({
  open,
  onClose,
  merchantId,
  rail,
}: RotateModalProps) {
  const qc = useQueryClient();

  const [creds, setCreds] = useState<CredFields>(EMPTY_CREDS);
  const [baseUrl, setBaseUrl] = useState("");
  // Issue #1: bank-rail-issued merchant_id. Required on rotation — see
  // CreateBankAccountPayload.provider_merchant_id for the contract.
  const [providerMerchantId, setProviderMerchantId] = useState("");
  // PDF round 3 N4: same env-lock policy as the add-rail modal.
  const [environment, setEnvironment] = useState<"sandbox" | "production">(
    defaultRailEnvironment(),
  );
  const [submitting, setSubmitting] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [succeeded, setSucceeded] = useState(false);

  function resetAndClose() {
    // Clear credential state on close (T-3-15).
    setCreds(EMPTY_CREDS);
    setBaseUrl("");
    setProviderMerchantId("");
    setEnvironment(defaultRailEnvironment());
    setSubmitting(false);
    setVerifyError(null);
    setGeneralError(null);
    setSucceeded(false);
    onClose();
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!rail) return;
    setVerifyError(null);
    setGeneralError(null);
    setSubmitting(true);

    try {
      const result = await rotateBankAccountCredentials(merchantId, rail.id, {
        email: creds.email.trim(),
        password: creds.password,
        merchant_key: creds.merchant_key,
        base_url: baseUrl.trim(),
        environment,
        provider_merchant_id: providerMerchantId.trim(),
      });

      if (result.active) {
        setSucceeded(true);
        void qc.invalidateQueries({ queryKey: ["bank-accounts", merchantId] });
        setTimeout(() => resetAndClose(), 1200);
      } else {
        setVerifyError(
          result.verify_error ??
            "Credentials could not be verified. The rail remains inactive — try again with correct credentials.",
        );
      }
    } catch (err) {
      const code = extractErrorCode(err);
      if (code === "base_url_invalid") {
        setGeneralError("The base URL must be a valid https URL.");
      } else if (code === "environment_invalid") {
        setGeneralError("Select a valid environment (sandbox or production).");
      } else if (code === "provider_merchant_id_missing") {
        setGeneralError(
          "Bank merchant ID is required. It's the alphanumeric ID the bank rail issued during onboarding (e.g. 29V2155SVGITOGND).",
        );
      } else {
        setGeneralError(
          "An unexpected error occurred. Please try again or contact support.",
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={resetAndClose}
      busy={submitting}
      title="Rotate credentials"
      foot={
        succeeded ? (
          <div className="row gap-8" style={{ color: "var(--pos)", fontSize: 13 }}>
            <I.Check size={14} stroke={2} /> Credentials rotated
          </div>
        ) : verifyError ? (
          <button className="btn" type="button" onClick={resetAndClose}>
            Close
          </button>
        ) : (
          <>
            <button
              className="btn ghost"
              type="button"
              onClick={resetAndClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <div className="flex-1" />
            <button
              className="btn accent"
              type="submit"
              form="rotate-creds-form"
              disabled={
                submitting ||
                !baseUrl.trim() ||
                !creds.email.trim() ||
                !creds.password ||
                !creds.merchant_key ||
                !providerMerchantId.trim()
              }
            >
              {submitting ? "Rotating…" : "Rotate credentials"}
            </button>
          </>
        )
      }
    >
      <form id="rotate-creds-form" onSubmit={handleSubmit} className="stack-16">
        {rail && (
          <div
            style={{
              background: "var(--bg-sunk)",
              padding: "10px 12px",
              borderRadius: 3,
              fontSize: 12,
            }}
          >
            <span className="dim">Rail: </span>
            <strong>{rail.label}</strong>
            <span className="dim"> · {rail.provider}</span>
          </div>
        )}

        <div className="field">
          <label className="label" htmlFor="rotate-env">
            Environment
          </label>
          {canPickRailEnvironment() ? (
            <select
              id="rotate-env"
              className="input"
              value={environment}
              onChange={(e) =>
                setEnvironment(e.target.value as "sandbox" | "production")
              }
            >
              <option value="sandbox">Sandbox</option>
              <option value="production">Production</option>
            </select>
          ) : (
            <div
              id="rotate-env"
              className="input"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                background: "var(--bg-sunk)",
                color: "var(--ink-2)",
                cursor: "default",
              }}
            >
              <span>{lockedRailEnvironmentLabel()}</span>
              <span className="dim" style={{ fontSize: 11 }}>
                · locked to match this deployment
              </span>
            </div>
          )}
        </div>

        <div className="field">
          <label className="label" htmlFor="rotate-base-url">
            Bank rail base URL
          </label>
          <input
            id="rotate-base-url"
            className="input mono"
            type="url"
            placeholder="https://sandbox.yajo.in/"
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            autoComplete="off"
            required
          />
          <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
            Must be an https URL. Stored on the new credential row.
          </div>
        </div>

        {/* Issue #1: bank-rail-issued merchant_id, re-anchored on every rotation. */}
        <div className="field">
          <label className="label" htmlFor="rotate-provider-mid">
            Bank merchant ID
          </label>
          <input
            id="rotate-provider-mid"
            className="input mono"
            type="text"
            placeholder="e.g. 29V2155SVGITOGND"
            value={providerMerchantId}
            onChange={(e) => setProviderMerchantId(e.target.value)}
            autoComplete="off"
            required
          />
          <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
            The alphanumeric ID the bank rail issued during onboarding.
            Re-anchored with every rotation; required separately from the
            credential triple. Wrong value triggers <code>072 "Empty or
            invalid merchant id"</code> on balance / payout calls.
          </div>
        </div>

        <div
          style={{
            background: "var(--bg-sunk)",
            borderLeft: "2px solid var(--hair)",
            padding: "12px 14px",
            borderRadius: 3,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 10 }}>
            New bank rail credentials
          </div>
          <div className="stack-12">
            <div className="field">
              <label className="label" htmlFor="rotate-email">
                User ID / Email
              </label>
              <input
                id="rotate-email"
                className="input mono"
                type="text"
                autoComplete="username"
                value={creds.email}
                onChange={(e) =>
                  setCreds((p) => ({ ...p, email: e.target.value }))
                }
                required
              />
            </div>

            <div className="field">
              <label className="label" htmlFor="rotate-password">
                Password
              </label>
              <input
                id="rotate-password"
                className="input"
                type="password"
                autoComplete="new-password"
                value={creds.password}
                onChange={(e) =>
                  setCreds((p) => ({ ...p, password: e.target.value }))
                }
                required
              />
            </div>

            <div className="field">
              <label className="label" htmlFor="rotate-merchant-key">
                Merchant key
              </label>
              <input
                id="rotate-merchant-key"
                className="input mono"
                type="password"
                autoComplete="new-password"
                value={creds.merchant_key}
                onChange={(e) =>
                  setCreds((p) => ({ ...p, merchant_key: e.target.value }))
                }
                required
              />
            </div>
          </div>
        </div>

        <div className="dim" style={{ fontSize: 11 }}>
          The existing credentials are retired immediately. If the new
          credentials fail verification, the rail stays inactive.
        </div>

        {verifyError && (
          <div
            // AUDIT-PASS-6 P-1: see matching block in AddRailModal — error
            // state, not warning state. --neg, not --warn.
            style={{
              background: "var(--neg-soft)",
              color: "var(--neg)",
              padding: "10px 12px",
              borderRadius: 3,
              fontSize: 12,
            }}
          >
            <strong>Credentials could not be verified:</strong> {verifyError}.
            The rail remains inactive — try again with correct credentials.
          </div>
        )}

        {generalError && (
          <div style={{ color: "var(--neg)", fontSize: 12 }}>
            {generalError}
          </div>
        )}
      </form>
    </Modal>
  );
}

// ─── Rail row — one configured rail, with a live credential health check ─────

function RailRow({
  rail,
  merchantId,
  onRotate,
  showProvider,
  showRotate,
}: {
  rail: BankAccount;
  merchantId: number;
  onRotate: () => void;
  /** Provider column is admin-only — merchants see their funding rails by
      label only. Hiding the provider here keeps the upstream identity
      out of the merchant view; the wider page hides the header in the
      same render branch. */
  showProvider: boolean;
  /** Rotate credentials is platform-admin only. The backend gates the
      endpoint at the membership-owner layer; this UI flag removes
      the affordance for merchants entirely so the row stays read-only
      from their view. "Test connection" stays — it's a non-mutating
      health probe everyone can use. */
  showRotate: boolean;
}) {
  const qc = useQueryClient();
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<VerifyResult | null>(null);
  // Per-rail auto-approve (backend migration 034). Admin-only toggle —
  // showRotate is reused as the admin signal, same pattern as the Test
  // and Rotate buttons. Merchants see the rail row but cannot edit.
  const [autoApprove, setAutoApprove] = useState<boolean>(
    rail.auto_approve ?? false,
  );
  const [autoSaving, setAutoSaving] = useState(false);
  // The rail's off switch (backend migration 050). Undefined on an older
  // response means not disabled, matching the column default.
  const disabled = rail.disabled ?? false;
  // Disabling asks for a reason; re-enabling does not, so only one of the two
  // directions opens a dialog.
  const [confirmingDisable, setConfirmingDisable] = useState(false);
  const [disableReason, setDisableReason] = useState("");
  const [disableSaving, setDisableSaving] = useState(false);
  const [disableError, setDisableError] = useState<string | null>(null);
  // Ordering block for a fixed-format file rail. Editable after creation
  // because a rail set up before the provider existed — or created with the
  // block half-filled — otherwise has no way to be completed, and every
  // export fails naming a field the operator cannot reach.
  const isFixedFormat = rail.provider === "canara_bank";
  const [editingSettings, setEditingSettings] = useState(false);
  const [fr, setFr] = useState<FileRailSettings>(rail.file_rail_settings ?? {});
  const [frSaving, setFrSaving] = useState(false);
  const [frError, setFrError] = useState<string | null>(null);
  // The account transfers are debited from. Editable because a rail created
  // with a typo here — one in production has the words "canara bank" in both
  // fields — could otherwise never produce a valid file.
  const [debitAcct, setDebitAcct] = useState(rail.account_no ?? "");
  const [debitIFSC, setDebitIFSC] = useState(rail.ifsc ?? "");
  const debitValid =
    /^[0-9]+$/.test(debitAcct.trim()) &&
    /^[A-Z]{4}0[A-Z0-9]{6}$/.test(debitIFSC.trim().toUpperCase());
  const frReady =
    (fr.ordering_customer_name ?? "").trim() !== "" &&
    (fr.address_line_1 ?? "").trim() !== "";

  async function runTest() {
    setTesting(true);
    setResult(null);
    try {
      const r = await verifyBankAccount(merchantId, rail.id);
      setResult(r);
      // A passing check can heal a pending rail to active — refresh the list.
      if (r.ok) {
        void qc.invalidateQueries({ queryKey: ["bank-accounts", merchantId] });
      }
    } catch {
      setResult({
        ok: false,
        verify_error: "Request failed — could not reach the backend.",
      });
    } finally {
      setTesting(false);
    }
  }

  async function saveFileRailSettings() {
    setFrSaving(true);
    setFrError(null);
    try {
      await updateBankAccount(merchantId, rail.id, {
        debit_account_no: debitAcct.trim(),
        debit_ifsc: debitIFSC.trim().toUpperCase(),
        file_rail_settings: {
          ...fr,
          // The payee's own bank, so on-us payees settle internally rather
          // than by NEFT. A property of which bank the rail is at, not a
          // choice the operator makes per rail.
          on_us_ifsc_prefix: fr.on_us_ifsc_prefix || "CNRB",
        },
      });
      void qc.invalidateQueries({ queryKey: ["bank-accounts", merchantId] });
      setEditingSettings(false);
    } catch (e) {
      setFrError(apiErrorMessage(e, "The rail settings could not be saved."));
    } finally {
      setFrSaving(false);
    }
  }

  async function toggleAutoApprove(next: boolean) {
    const prev = autoApprove;
    setAutoApprove(next); // optimistic
    setAutoSaving(true);
    try {
      await updateBankAccount(merchantId, rail.id, { auto_approve: next });
      void qc.invalidateQueries({ queryKey: ["bank-accounts", merchantId] });
    } catch {
      // Roll back on failure — keep the rail row in sync with reality.
      setAutoApprove(prev);
    } finally {
      setAutoSaving(false);
    }
  }

  async function setDisabled(next: boolean) {
    setDisableSaving(true);
    setDisableError(null);
    try {
      await updateBankAccount(merchantId, rail.id, {
        disabled: next,
        // Only sent when switching off. The backend ignores it in the other
        // direction and deliberately keeps the previous reason as history.
        ...(next ? { disabled_reason: disableReason.trim() } : {}),
      });
      void qc.invalidateQueries({ queryKey: ["bank-accounts", merchantId] });
      setConfirmingDisable(false);
      setDisableReason("");
    } catch (e) {
      // No optimistic update here, unlike the auto-approve toggle. Showing a
      // rail as stopped before the backend agrees is the one direction that
      // could get somebody to stand down during an incident while payouts are
      // still going out.
      setDisableError(
        apiErrorMessage(e, "The rail could not be switched off."),
      );
    } finally {
      setDisableSaving(false);
    }
  }

  const disableModal = confirmingDisable ? (
    <Modal
      open
      title={`Disable ${rail.label}?`}
      onClose={() => {
        setConfirmingDisable(false);
        setDisableError(null);
      }}
      foot={
        <>
          <button
            className="btn ghost"
            type="button"
            onClick={() => {
              setConfirmingDisable(false);
              setDisableError(null);
            }}
            disabled={disableSaving}
          >
            Cancel
          </button>
          <button
            className="btn danger"
            type="button"
            onClick={() => void setDisabled(true)}
            disabled={disableSaving}
          >
            {disableSaving ? "Disabling…" : "Disable rail"}
          </button>
        </>
      }
    >
      <div className="stack-12">
        <p style={{ margin: 0, fontSize: 13 }}>
          New payouts on this rail will be refused, routing rules that name it
          will skip it whatever their priority, and payouts already waiting for
          approval on it will not dispatch until it is switched back on.
        </p>
        <p className="dim" style={{ margin: 0, fontSize: 12 }}>
          Nothing is deleted and nothing already queued is failed — re-enabling
          lets the queue carry on where it stopped.
        </p>
        <div className="field">
          <label className="label" htmlFor={`dis-reason-${rail.id}`}>
            Reason <span className="dim">(optional)</span>
          </label>
          <input
            id={`dis-reason-${rail.id}`}
            className="input"
            value={disableReason}
            onChange={(e) => setDisableReason(e.target.value)}
            placeholder="Shown beside the rail and in the rejection"
            maxLength={500}
            autoComplete="off"
          />
        </div>
        {disableError && (
          <p className="hint neg" style={{ fontSize: 12 }}>
            {disableError}
          </p>
        )}
      </div>
    </Modal>
  ) : null;

  const settingsModal = editingSettings ? (
    <Modal
      open
      title={`File settings — ${rail.label}`}
      onClose={() => setEditingSettings(false)}
      foot={
        <>
          <button
            className="btn ghost"
            type="button"
            onClick={() => setEditingSettings(false)}
          >
            Cancel
          </button>
          <button
            className="btn"
            type="button"
            disabled={frSaving || !frReady || !debitValid}
            onClick={() => void saveFileRailSettings()}
          >
            {frSaving ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <div className="stack-16">
        <p className="dim" style={{ fontSize: 12 }}>
          Stamped on every record of the bulk file. The name and first address
          line are mandatory — the bank will not accept a file without them.
        </p>
        <div className="field">
          <label className="label" htmlFor={`fr-acct-${rail.id}`}>
            Debit account number
          </label>
          <input
            id={`fr-acct-${rail.id}`}
            className="input"
            value={debitAcct}
            onChange={(e) => setDebitAcct(e.target.value)}
            placeholder="Digits only — the account the transfers leave from"
            autoComplete="off"
          />
        </div>
        <div className="field">
          <label className="label" htmlFor={`fr-ifsc-${rail.id}`}>
            Debit IFSC
          </label>
          <input
            id={`fr-ifsc-${rail.id}`}
            className="input"
            value={debitIFSC}
            onChange={(e) => setDebitIFSC(e.target.value)}
            placeholder="e.g. CNRB0000281"
            autoComplete="off"
            style={{ textTransform: "uppercase" }}
          />
          {debitIFSC.trim() !== "" &&
            !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(debitIFSC.trim().toUpperCase()) && (
              <div className="hint neg" style={{ fontSize: 11, marginTop: 4 }}>
                An IFSC is 11 characters: four letters, then a zero, then six
                more. The zero is the one people drop.
              </div>
            )}
        </div>
        <div className="field">
          <label className="label" htmlFor={`fr-name-${rail.id}`}>
            Ordering customer name
          </label>
          <input
            id={`fr-name-${rail.id}`}
            className="input"
            value={fr.ordering_customer_name ?? ""}
            onChange={(e) =>
              setFr({ ...fr, ordering_customer_name: e.target.value })
            }
            placeholder="The name the payee sees on their statement"
            autoComplete="off"
          />
        </div>
        <div className="field">
          <label className="label" htmlFor={`fr-a1-${rail.id}`}>
            Address line 1
          </label>
          <input
            id={`fr-a1-${rail.id}`}
            className="input"
            value={fr.address_line_1 ?? ""}
            onChange={(e) => setFr({ ...fr, address_line_1: e.target.value })}
            placeholder="Required — the bank rejects a file without it"
            autoComplete="off"
          />
        </div>
        <div className="field">
          <label className="label" htmlFor={`fr-a2-${rail.id}`}>
            Address line 2 <span className="dim">(optional)</span>
          </label>
          <input
            id={`fr-a2-${rail.id}`}
            className="input"
            value={fr.address_line_2 ?? ""}
            onChange={(e) => setFr({ ...fr, address_line_2: e.target.value })}
            autoComplete="off"
          />
        </div>
        <div className="field">
          <label className="label" htmlFor={`fr-info-${rail.id}`}>
            Statement narration <span className="dim">(optional)</span>
          </label>
          <input
            id={`fr-info-${rail.id}`}
            className="input"
            value={fr.sender_to_receiver_info ?? ""}
            onChange={(e) =>
              setFr({ ...fr, sender_to_receiver_info: e.target.value })
            }
            placeholder="Defaults to PAYOUT"
            autoComplete="off"
          />
        </div>
        {frError && (
          <p className="hint neg" style={{ fontSize: 12 }}>
            {frError}
          </p>
        )}
      </div>
    </Modal>
  ) : null;

  return (
    // Dimmed so a stopped rail reads as inert at a glance on a page of
    // working ones. Opacity only — every control stays reachable, because
    // the one thing an operator most needs on a disabled rail is the button
    // that enables it again.
    <tr style={disabled ? { opacity: 0.55 } : undefined}>
      <td>
        {settingsModal}
        {disableModal}
        <div className="cell-name">{rail.label}</div>
        <div className="cell-id">{rail.id}</div>
      </td>
      {showProvider && (
        <td className="dim">{providerLabel(rail.provider)}</td>
      )}
      <td className="mono">{rail.currency}</td>
      <td>
        <StatusPill status={rail.status} />
        {disabled && rail.disabled_reason && (
          <div
            className="dim"
            style={{ fontSize: 11, marginTop: 3, maxWidth: 220 }}
            title={rail.disabled_reason}
          >
            {rail.disabled_reason}
          </div>
        )}
      </td>
      <td className="dim mono" style={{ fontSize: 11 }}>
        {formatDateTime(rail.created_at)}
      </td>
      <td className="right">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            justifyContent: "flex-end",
          }}
        >
          {showRotate && (
            // Per-rail auto-approve toggle (admin-only). When on, payouts
            // on this rail dispatch inline; off → land in /approvals.
            // Migration 034 moved this off merchants onto bank_accounts.
            <label
              className="row gap-8"
              style={{ alignItems: "center", cursor: autoSaving ? "wait" : "pointer", fontSize: 11 }}
              title="Auto-approve every payout on this rail (skip the admin approval queue)."
            >
              <input
                type="checkbox"
                checked={autoApprove}
                disabled={autoSaving || disabled}
                onChange={(e) => toggleAutoApprove(e.target.checked)}
              />
              <span className="dim">Auto-approve</span>
            </label>
          )}
          {result && (
            <span
              style={{
                fontSize: 11,
                color: result.ok ? "var(--ok, #1a7f37)" : "var(--warn, #b42318)",
                maxWidth: 260,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={
                result.ok
                  ? "Bank rail credentials authenticated successfully"
                  : result.verify_error
              }
            >
              {result.ok
                ? "✓ Credentials valid"
                : `✕ ${result.verify_error ?? "Credentials failed"}`}
            </span>
          )}
          {/* PDF review 26 May 26: Test connection is admin-only too —
              `showRotate` here is reused as the "this user is platform
              admin" signal since both controls are gated on the same
              role. Merchants see the rail row but cannot probe
              credentials or rotate them; they get the label, currency,
              status, and Added timestamp. Read-only. */}
          {showRotate && (
            <button
              className="btn ghost sm"
              type="button"
              onClick={runTest}
              disabled={testing}
            >
              {testing ? "Testing…" : "Test connection"}
            </button>
          )}
          {showRotate && isFixedFormat && (
            <button
              className="btn ghost sm"
              type="button"
              onClick={() => setEditingSettings(true)}
              title="The corporate name and address stamped on every record of the bulk file."
            >
              {frReady ? "File settings" : "Finish setup"}
            </button>
          )}
          {/* Rotation seals the IDFC rail's credential shape; the backend
              refuses it (409) on any other rail, where it would replace the
              rail's own keys with fields nothing reads. */}
          {showRotate && rail.provider === "idfc" && (
            <button className="btn ghost sm" type="button" onClick={onRotate}>
              Rotate credentials
            </button>
          )}
          {/* The off switch (backend migration 050). Admin-only on the same
              `showRotate` signal as every other mutating control here.
              Rightmost and last, because it is the most consequential thing
              on the row — it stops every payout aimed at this rail. */}
          {showRotate &&
            (disabled ? (
              <button
                className="btn ghost sm"
                type="button"
                onClick={() => void setDisabled(false)}
                disabled={disableSaving}
                title="Accept payouts on this rail again. Anything waiting for approval becomes dispatchable."
              >
                {disableSaving ? "Enabling…" : "Enable"}
              </button>
            ) : (
              <button
                className="btn ghost sm"
                type="button"
                onClick={() => {
                  setDisableReason("");
                  setDisableError(null);
                  setConfirmingDisable(true);
                }}
                title="Stop payouts on this rail. Routing rules that name it will skip it."
              >
                Disable
              </button>
            ))}
        </div>
      </td>
    </tr>
  );
}

// ─── BankAccounts page ────────────────────────────────────────────────────────

export function BankAccounts() {
  const merchantId = useUI((s) => s.currentMerchantId);
  const { data: accounts = [], isLoading } = useBankAccounts();
  // Provider column is admin-only. Merchant users see their rails by
  // label / status only; the upstream provider identity (IDFC bank rail,
  // Buckzy) stays internal — keeps the merchant view scoped to "is
  // this rail working" without revealing operational topology.
  const isAdmin = useAuth((s) => isAdminRole(s.user?.role));

  const [addOpen, setAddOpen] = useState(false);
  const [buckzyOpen, setBuckzyOpen] = useState(false);
  const [rotateRail, setRotateRail] = useState<BankAccount | null>(null);

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="eyebrow">Banking</div>
          <h1>Bank rails</h1>
          <div className="sub">
            {isLoading
              ? "Loading…"
              : `${accounts.length} rail${accounts.length === 1 ? "" : "s"} configured`}
          </div>
        </div>
        <div className="actions">
          {/* Bank-rail provisioning is platform-admin only. Merchant
              owners/operators see their rails read-only — they don't
              add or rotate credentials. The backend gates these
              endpoints at the admin-role layer (matching the panel
              intent), so the UI guard here is the affordance-removal
              half: no buttons means no clicks that 403 anyway. */}
          {merchantId != null && isAdmin && (
            <>
              <button
                className="btn"
                type="button"
                onClick={() => setBuckzyOpen(true)}
              >
                <I.Plus size={12} /> Add Buckzy connection
              </button>
              <button
                className="btn accent"
                type="button"
                onClick={() => setAddOpen(true)}
              >
                <I.Plus size={12} /> Add bank rail
              </button>
            </>
          )}
        </div>
      </div>

      <div className="card">
        {merchantId == null ? (
          <div className="empty">
            <div className="icon">
              <I.Banks size={16} />
            </div>
            <h3>No merchant selected</h3>
            <p>
              Bank rails are per-merchant. Pick a merchant from the switcher in
              the top bar to view and add rails.
            </p>
          </div>
        ) : isLoading ? (
          // Skeleton table — visually distinct from both the "pick a
          // merchant" and "no rails yet" empty states (audit pass-3 C3).
          <table className="tbl">
            <thead>
              <tr>
                <th>Label</th>
                {isAdmin && <th>Provider</th>}
                <th>Currency</th>
                <th>Status</th>
                <th>Added</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 3 }).map((_, i) => (
                <tr key={i}>
                  <td><span className="skel" style={{ width: 140, height: 12, display: "inline-block" }} /></td>
                  {isAdmin && (
                    <td><span className="skel" style={{ width: 90, height: 12, display: "inline-block" }} /></td>
                  )}
                  <td><span className="skel" style={{ width: 40, height: 12, display: "inline-block" }} /></td>
                  <td><span className="skel" style={{ width: 56, height: 18, display: "inline-block" }} /></td>
                  <td><span className="skel" style={{ width: 90, height: 12, display: "inline-block" }} /></td>
                  <td />
                </tr>
              ))}
            </tbody>
          </table>
        ) : accounts.length === 0 ? (
          <div className="empty">
            <div className="icon">
              <I.Banks size={16} />
            </div>
            <h3>No rails configured</h3>
            {/* Different copy + CTA depending on who's looking:
                admins see the action they can take; merchants see a
                pointer to ask the platform team. */}
            {isAdmin ? (
              <>
                <p>
                  Add a bank rail so this merchant can disburse funds.
                  Credentials are envelope-encrypted and never stored in
                  plain text.
                </p>
                <button
                  className="btn primary"
                  type="button"
                  onClick={() => setAddOpen(true)}
                >
                  <I.Plus size={12} /> Add bank rail
                </button>
              </>
            ) : (
              <p>
                No bank rails have been configured for this merchant yet.
                Contact your platform administrator to provision one — it's
                needed before any payouts can be initiated.
              </p>
            )}
          </div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Label</th>
                {isAdmin && <th>Provider</th>}
                <th>Currency</th>
                <th>Status</th>
                <th>Added</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {accounts.map((a) => (
                <RailRow
                  key={a.id}
                  rail={a}
                  merchantId={merchantId}
                  onRotate={() => setRotateRail(a)}
                  showProvider={isAdmin}
                  showRotate={isAdmin}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {merchantId != null && (
        <AddRailModal
          open={addOpen}
          onClose={() => setAddOpen(false)}
          merchantId={merchantId}
        />
      )}

      {merchantId != null && (
        <AddBuckzyModal
          open={buckzyOpen}
          onClose={() => setBuckzyOpen(false)}
          merchantId={merchantId}
        />
      )}

      {merchantId != null && (
        <RotateCredentialsModal
          open={rotateRail != null}
          onClose={() => setRotateRail(null)}
          merchantId={merchantId}
          rail={rotateRail}
        />
      )}
    </div>
  );
}
